import { Button, Input } from 'antd';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ReactSVG } from 'react-svg';
import { COLORS, DAYTIME_FORMAT, DAY_FORMAT, ORDER_TYPE, STEP_TYPE } from '../../../constants';
import BaseTable from '../Base';

const RouteOrdersTable = ({ dataSource, isFetching, routePlan, query, riders, mapData = {}, ...props }) => {
  const history = useHistory();
  const [searchedRiders, setSearchedRiders] = useState(dataSource || []);
  const [searchValue, setSearchValue] = useState('');

  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || 100;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: searchedRiders.length,
    showTotal: (total) => `${total} Orders`,
  };

  useEffect(() => {
    setSearchedRiders(dataSource);
  }, [dataSource]);

  useEffect(() => {
    let orderT = dataSource;
    if (!isEmpty(searchValue)) {
      orderT = filter(dataSource, (order) => {
        return (
          order.orderId?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order?.orderRef?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.orderType?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.pickupName?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.pickupAddress?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.pickupPhone?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.deliveryName?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.deliveryAddress?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.deliveryPhone?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.load?.toString().toLowerCase().match(searchValue.toLowerCase()) ||
          order.status?.toString().toLowerCase().match(searchValue.toLowerCase())
        );
      });
    }
    setSearchedRiders(orderT);
  }, [searchValue]);

  const addRidersToRoute = () => {
    history.push('/routes/' + routePlan.slug + '/add-orders');
  };

  const handleOnSearch = (e) => {
    setSearchValue(e.target.value || '');
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      fixed: 'left',
      width: 150,
      render: (value, row) => {
        const id = row.id;
        const riderId = row.riderId;
        const color = COLORS[riderId % COLORS.length];
        const steps = mapData[riderId]?.steps;
        const deliveryIndex = steps?.findIndex((s) => s.type === STEP_TYPE.delivery && s.orderId === id);
        const pickupIndex = steps?.findIndex((s) => s.type === STEP_TYPE.pickup && s.orderIds.includes(id));
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {pickupIndex > -1 && (
              <img
                src={routePlan.urlPhoto + steps[pickupIndex].logoFile}
                style={{ maxWidth: 22, maxHeight: 22, border: `1px solid #${color}`, borderRadius: '50%' }}
                alt=""
              />
            )}
            {deliveryIndex > -1 && (
              <span
                style={{
                  border: `1px solid #${color}`,
                  color: `#${color}`,
                  backgroundColor: 'white',
                  textAlign: 'center',
                  minWidth: '22px',
                  minHeight: '22px',
                  lineHeight: '20px',
                  borderRadius: 4,
                  fontSize: '11px',
                  marginLeft: '6px',
                  letterSpacing: -1,
                  paddingRight: 1,
                }}
              >
                {deliveryIndex + 1}
              </span>
            )}
            <div style={{ paddingLeft: '12px' }}>{value}</div>
          </div>
        );
      },
    },
    {
      title: 'Order Ref',
      dataIndex: 'orderRef',
      key: 'orderRef',
      width: 150,
    },
    {
      title: 'Order Type',
      dataIndex: 'orderType',
      key: 'orderType',
      width: 120,
      render: (value) => {
        return (
          <div className={value + ' orderType'}>
            {' '}
            <ReactSVG className="icon" src={'/icons/' + value + '.svg'} /> {ORDER_TYPE[value]}
          </div>
        );
      },
    },
    {
      title: 'Rider',
      dataIndex: 'riderId',
      key: 'riderId',
      width: 150,
      render: (value) => {
        return riders.find((r) => r.riderId === value)?.user?.name || '';
      },
    },
    {
      title: 'Delivery Date',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (value) => {
        return value ? moment(value).format(DAY_FORMAT) : '';
      },
    },
    {
      title: 'Company',
      key: 'Company',
      className: 'order-companies',
      render: (row) => {
        if (row.location?.company) {
          return (
            <div>
              <div className="logo">
                <img src={row.location?.company?.logoThumbUrl || row.location?.company?.logoUrl} />
              </div>
              {row.location?.company?.name}
            </div>
          );
        }
      },
    },
    {
      title: 'Pickup Name',
      dataIndex: 'pickupName',
      key: 'pickupName',
    },
    {
      title: 'Pickup Address',
      dataIndex: 'pickupAddress',
      width: 700,
      key: 'pickupAddress',
    },
    {
      title: 'Pickup Phone',
      dataIndex: 'pickupPhone',
      key: 'pickupPhone',
    },
    {
      title: 'Pickup Time Window',
      dataIndex: 'Pickup Time Window',
      key: 'Pickup Time Window',
      render: (value, row) => {
        return (row.pickupTimeStart || '') + ' - ' + (row.pickupTimeEnd || '');
      },
    },
    {
      title: 'Delivery Name',
      dataIndex: 'deliveryName',
      key: 'deliveryName',
    },
    {
      title: 'Delivery Address',
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      width: 700,
    },
    {
      title: 'Delivery Phone',
      dataIndex: 'deliveryPhone',
      key: 'deliveryPhone',
    },
    {
      title: 'Delivery Time Window',
      dataIndex: 'Delivery Time Window',
      key: 'Delivery Time Window',
      render: (value, row) => {
        return (row.deliveryTimeStart || '') + ' - ' + (row.deliveryTimeEnd || '');
      },
    },
    {
      title: 'Load',
      dataIndex: 'load',
      key: 'load',
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => {
        return value ? moment(value).format(DAYTIME_FORMAT) : '';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      fixed: 'right',
      width: 160,
      key: 'status',
      render: (value) => {
        return <span className={'status orders ' + value.toLowerCase()}>{value?.replace('_', ' ')}</span>;
      },
    },
  ];

  return (
    <div className="RouteOrdersTable table-common">
      <div className="riders-header table-header">
        <div className="search full">
          <Input
            onChange={handleOnSearch}
            placeholder="Search"
            suffix={<img src="/icons/search.svg" />}
            disabled={isFetching}
            value={searchValue}
          />
        </div>
        <div className="buttons">
          <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={addRidersToRoute}>
            Add Order
            <ReactSVG className="icon" src="/icons/plus.svg" />
          </Button>
        </div>
      </div>
      <BaseTable
        columns={columns}
        scroll={{ x: 4200, y: 'calc(100vh - 400px)' }}
        dataSource={searchedRiders}
        pagination={pagination}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
    </div>
  );
};

export default RouteOrdersTable;
